import { Card, Box, TextField, Button, FormControl, InputLabel, Select, MenuItem, Alert, CircularProgress } from "@mui/material";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateScreenTitle, showSuccessMessage, showLoader, dismissLoader } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";
import WDBox from "../../../../MaterialComponents/WDBox";
import partnerAPI from "../../../../services/channelPartnerService";
import { useNavigate } from "react-router-dom";

const ChannelPartnerUserAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    userName: "",
    password: "",
    email: "",
    phone: "",
    roleCode: "CHANNEL_PARTNER_ADMIN",
    selectedPartnerId: ""
  });

  useEffect(() => {
    dispatch(updateScreenTitle("Add Channel Partner User"));
    fetchPartners();
    return () => dispatch(updateScreenTitle(""));
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await partnerAPI.get("/api/channel-partners/onboarding/all");
      setPartners(Array.isArray(response.data) ? response.data : []);
      if (Array.isArray(response.data) && response.data.length > 0) {
        setFormData(prev => ({ ...prev, selectedPartnerId: response.data[0].id }));
      }
    } catch (error) {
      console.error("Error fetching partners:", error);
      dispatch(showSuccessMessage("Error fetching channel partners"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.userName || !formData.password || !formData.email || !formData.phone) {
      dispatch(showSuccessMessage("Please fill all required fields"));
      return;
    }

    if (!formData.selectedPartnerId) {
      dispatch(showSuccessMessage("Please select a channel partner"));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      dispatch(showSuccessMessage("Please enter a valid email address"));
      return;
    }

    dispatch(showLoader());
    try {
      const payload = {
        name: formData.name,
        userName: formData.userName,
        password: formData.password,
        email: formData.email,
        phone: formData.phone,
        roleCode: formData.roleCode
      };

      await partnerAPI.post(
        `/api/channel-partners/${formData.selectedPartnerId}/users`,
        payload
      );

      dispatch(showSuccessMessage("User added successfully!"));
      setTimeout(() => {
        navigate("/ADM-ChannelPartners/adm-channel-partner-users");
      }, 1500);
    } catch (error) {
      console.error("Error adding user:", error);
      dispatch(showSuccessMessage(error.response?.data?.message || "Error adding user"));
    } finally {
      dispatch(dismissLoader());
    }
  };

  if (loading) {
    return (
      <WDBox display="flex" justifyContent="center" alignItems="center" py={10}>
        <CircularProgress />
      </WDBox>
    );
  }

  return (
    <WDBox py={3}>
      <Card>
        <Box p={4} maxWidth={600} mx="auto">
          <h2 style={{ marginBottom: 24, textAlign: "center" }}>Add New Channel Partner User</h2>

          <Alert severity="info" sx={{ mb: 3 }}>
            Select a channel partner and fill in the user details below.
          </Alert>

          <form onSubmit={handleSubmit}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Select Channel Partner</InputLabel>
              <Select
                value={formData.selectedPartnerId}
                onChange={(e) => setFormData({ ...formData, selectedPartnerId: e.target.value })}
                label="Select Channel Partner"
              >
                {partners.map((partner) => (
                  <MenuItem key={partner.id} value={partner.id}>
                    {partner.organisationName || "Unknown"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              placeholder="e.g., John Doe"
              required
            />

            <TextField
              fullWidth
              label="Username"
              value={formData.userName}
              onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
              margin="normal"
              placeholder="e.g., johndoe"
              required
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              placeholder="e.g., john@example.com"
              required
            />

            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              margin="normal"
              placeholder="e.g., +1234567890"
              required
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              margin="normal"
              placeholder="Enter a secure password"
              required
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.roleCode}
                onChange={(e) => setFormData({ ...formData, roleCode: e.target.value })}
                label="Role"
              >
                <MenuItem value="CHANNEL_PARTNER_ADMIN">Admin</MenuItem>
                <MenuItem value="CHANNEL_PARTNER_USER">User</MenuItem>
              </Select>
            </FormControl>

            <Box display="flex" gap={2} mt={4}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => navigate("/ADM-ChannelPartners/adm-channel-partner-users")}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
              >
                Add User
              </Button>
            </Box>
          </form>
        </Box>
      </Card>
    </WDBox>
  );
};

export default ChannelPartnerUserAdd;
